/* eslint-disable camelcase */
import { z } from 'zod'

import {
  type MeetingReportItem,
  type ReportDimension,
  reportDimensions,
  type ReportFilters,
  type ReportSummary,
  type ReportType,
  reportTypes,
  severities,
  type Severity,
} from '@/api/meetingReportsObservability'

export const dimensionLabels: Record<ReportDimension, string> = {
  signature: 'Signature',
  output_device_family: 'Output device family',
  output_device: 'Output device (exact label)',
  mic_device: 'Mic device',
  meeting_platform: 'Meeting platform',
  app_version: 'App version',
  known_issue: 'Known issue label',
  job_status: 'Job status',
  report_category: 'Report category',
  report_type: 'Report type',
  severity: 'Severity',
}

export const derivedDimensions: ReadonlySet<ReportDimension> = new Set<ReportDimension>([
  'output_device_family',
  'app_version',
])

export const severityLabels: Record<Severity, string> = {
  s0: 'S0 · data loss',
  s1: 'S1 · severe',
  s2: 'S2 · degraded',
  s3: 'S3 · minor',
}

export const windows = ['1d', '7d', '14d', '30d', '90d'] as const
export type Window = (typeof windows)[number]
const windowDays: Record<Window, number> = {
  '1d': 1,
  '7d': 7,
  '14d': 14,
  '30d': 30,
  '90d': 90,
}

const label = z.string().trim().min(1).max(200)

const DEFAULTS = { window: '7d', group_by: 'signature' } as const satisfies {
  window: Window
  group_by: ReportDimension
}

export const stateSchema = z.object({
  window: z.enum(windows).catch(DEFAULTS.window),
  group_by: z.enum(reportDimensions).catch(DEFAULTS.group_by),
  severity: z.enum(severities).optional().catch(undefined),
  report_type: z.enum(reportTypes).optional().catch(undefined),
  search: label.optional().catch(undefined),
  signature: label.optional().catch(undefined),
  output_device_family: label.optional().catch(undefined),
  output_device: label.optional().catch(undefined),
  mic_device: label.optional().catch(undefined),
  meeting_platform: label.optional().catch(undefined),
  app_version: label.optional().catch(undefined),
  known_issue: label.optional().catch(undefined),
  job_status: label.optional().catch(undefined),
  report_category: label.optional().catch(undefined),
  wispr_user: z.enum(['true', 'false']).optional().catch(undefined),
  selected: label.optional().catch(undefined),
})

export type PageState = z.infer<typeof stateSchema>

export const filterKeys = [
  'severity',
  'report_type',
  'signature',
  'output_device_family',
  'output_device',
  'mic_device',
  'meeting_platform',
  'app_version',
  'known_issue',
  'job_status',
  'report_category',
  'wispr_user',
] as const satisfies readonly (keyof PageState)[]
export type FilterKey = (typeof filterKeys)[number]

export const filterKeyLabels: Record<FilterKey, string> = { ...dimensionLabels, wispr_user: 'Wispr.ai user' }

export const wisprUserValues = ['true', 'false'] as const
const wisprUserLabels: Record<(typeof wisprUserValues)[number], string> = { true: 'Yes', false: 'No' }

export const defaultState = (): PageState => ({ ...DEFAULTS })

export const readState = (search: URLSearchParams): PageState =>
  stateSchema.parse(Object.fromEntries(Array.from(search.entries()).map(([key, value]) => [key, value || undefined])))

export const writeState = (state: PageState): URLSearchParams => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(state)) {
    if (value === undefined) continue
    if (key === 'window' && value === DEFAULTS.window) continue
    if (key === 'group_by' && value === DEFAULTS.group_by) continue
    params.set(key, String(value))
  }
  return params
}

const DAY_MS = 86_400_000
/** Granularity of the window's end bound; the page restamps `now` at the same cadence. */
export const WINDOW_STEP_MS = 60_000

export const windowBounds = (window: Window, now: Date): { start_at: string; end_at: string } => {
  const end = new Date(Math.ceil(now.getTime() / WINDOW_STEP_MS) * WINDOW_STEP_MS)
  return {
    start_at: new Date(end.getTime() - windowDays[window] * DAY_MS).toISOString(),
    end_at: end.toISOString(),
  }
}

export const toApiFilters = (state: PageState, now: Date): ReportFilters => {
  const filters: ReportFilters = { ...windowBounds(state.window, now) }
  for (const key of filterKeys) {
    const value = state[key]
    if (value === undefined) continue
    if (key === 'severity') filters.severity = value as Severity
    else if (key === 'report_type') filters.report_type = value as ReportType
    else if (key === 'wispr_user') filters.wispr_user = value === 'true'
    else filters[key] = value
  }
  if (state.search) filters.search = state.search
  return filters
}

export const activeFilters = (state: PageState): [FilterKey, string][] =>
  filterKeys.flatMap((key) => {
    const value = state[key]
    return value === undefined ? [] : [[key, value]]
  })

/** A board row's own value, folded into filters so cohorts and reports describe just that row. */
export const groupFilters = (state: PageState, value: string, now: Date): ReportFilters => {
  const scoped: PageState = { ...state }
  if (state.group_by === 'severity') scoped.severity = value as Severity
  else if (state.group_by === 'report_type') scoped.report_type = value as ReportType
  else scoped[state.group_by] = value
  return toApiFilters(scoped, now)
}

export const formatCount = (value: number) => value.toLocaleString('en-US')

/** Whole-percent change, or null when there is no previous value to compare against. */
const deltaPercent = (current: number, previous: number): number | null =>
  previous === 0 ? null : Math.round(((current - previous) / previous) * 100) || 0

export const formatDelta = (current: number, previous: number): string => {
  const change = deltaPercent(current, previous)
  if (change === null) return current === 0 ? '±0' : 'new'
  return `${change > 0 ? '+' : ''}${change}%`
}

export const deltaTone = (current: number, previous: number): 'up' | 'down' | 'flat' => {
  const change = deltaPercent(current, previous) ?? (current === 0 ? 0 : 1)
  if (change === 0) return 'flat'
  return change > 0 ? 'up' : 'down'
}

export const formatPercent = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)}%` : '0%')

/** The worst severity present in a summary, used for the floor pill. */
export const floorSeverity = (summary: ReportSummary): Severity | null =>
  severities.find((severity) => summary.severity[severity] > 0) ?? null

export const formatValue = (dimension: ReportDimension | FilterKey, value: string): string => {
  if (dimension === 'severity' && severities.includes(value as Severity)) return severityLabels[value as Severity]
  if (dimension === 'wispr_user' && (value === 'true' || value === 'false')) return wisprUserLabels[value]
  return value
}

export const slackPermalink = (report: Pick<MeetingReportItem, 'slack_channel' | 'slack_message_ts'>) =>
  report.slack_channel && report.slack_message_ts
    ? `https://wispr-ai.slack.com/archives/${encodeURIComponent(report.slack_channel)}/p${encodeURIComponent(report.slack_message_ts.replace('.', ''))}`
    : null

const utcDate = (date: Date): [number, number, number] => [date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()]

/** Every UTC calendar date the window touches, first through last inclusive. */
export const trendDays = (startAt: string, endAt: string): string[] => {
  const days: string[] = []
  const last = Date.UTC(...utcDate(new Date(endAt)))
  for (let day = Date.UTC(...utcDate(new Date(startAt))); day <= last; day += DAY_MS) {
    days.push(new Date(day).toISOString().slice(0, 10))
  }
  return days
}
